import { property } from "lit/decorators.js";
import { ListingType } from "./search.js";
import { State } from "@lit-app/state";
import { atom, computed, action as makeAction } from "nanostores";
import { isServer } from "lit";
import { getCookie, setCookie } from "tiny-cookie";

export type ResultType =
  | "Region"
  | "Suburb"
  | "Property"
  | "Operator"
  | "HomeCareProvider";
export type Result = {
  name: string;
  type: ResultType;
  keys: Record<string, string>;
};

function listingTypeFromPathname(pathname: string) {
  if (pathname.startsWith("/retirement-living")) {
    return "retirement-living";
  } else if (pathname.startsWith("/aged-care")) {
    return "aged-care";
  } else if (pathname.startsWith("/home-care/service-provider")) {
    return "home-care-packages";
  } else if (pathname.startsWith("/home-care/package-provider")) {
    return "home-care-packages";
  } else if (pathname.startsWith("/home-care")) {
    return "home-care-packages";
  }
  return "aged-care";
}

function getTypeSlug(listingType: ListingType) {
  if (listingType === "aged-care") {
    return "aged-care";
  }
  if (listingType === "retirement-living") {
    return "retirement-living";
  }
  if (listingType === "home-care-packages") {
    return "home-care";
  }
  if (listingType === "home-care-provider") {
    return "home-care";
  }
}

function redirect(result: Result, listingType: ListingType) {
  let url = "/";
  const typeSlug = getTypeSlug(listingType);

  if (result.type === "HomeCareProvider") {
    const slug = result.keys.slug;
    url = `/home-care/listings/${slug}-${result.keys.ac101Id}`;
  }

  if (result.type === "Region") {
    const region = result.keys.slug;
    const state = result.keys.state.toLowerCase();
    url = `/${typeSlug}/${state}/${region}/region`;
  }
  if (result.type === "Suburb") {
    const suburb = result.keys.slug;
    const postcode = result.keys.postcode;
    const state = result.keys.state.toLowerCase();
    url = `/${typeSlug}/${state}/${suburb}/${postcode}`;
  }
  if (result.type === "Property") {
    const id = result.keys.ac101Id;
    const slug = result.keys.slug;
    const suburb = result.keys.suburb.toLowerCase().replaceAll(" ", "-");
    const state = result.keys.state.toLowerCase();
    url = `/listings/${state}/${suburb}/${slug}-${id}`;
  }
  if (result.type === "Operator") {
    const id = result.keys.ac101Id;
    const slug = result.keys.slug;
    url = `/operators/${slug}-${id}`;
  }
  return url;
}

export const listingType = atom<ListingType>(
  isServer ? "aged-care" : listingTypeFromPathname(location.pathname)
);

export const result = atom<Result | undefined>(
  isServer ? undefined : window.__AC101_RESULT__
);

export const action = computed([listingType, result], (listingType, result) => {
  if (result && listingType) {
    return redirect(result, listingType);
  }
});

export const shortlist = atom<string[]>(
  getCookie("shortlist")
    ?.split(",")
    .filter((x) => x) ?? []
);

shortlist.subscribe((items) => {
  setCookie("shortlist", items.join(","), {
    expires: 365,
    samesite: "lax",
    secure: true,
  });
});

export const addShortlistItem = makeAction(
  shortlist,
  "addShortlistItem",
  (store, id: string = "") => {
    store.set([...store.get(), id]);
  }
);

export const removeShortlistItem = makeAction(
  shortlist,
  "removeShortlistItem",
  (store, id: string) => {
    store.set(store.get().filter((i) => i !== id));
  }
);

declare global {
  interface Window {
    __AC101_RESULT__?: Result;
  }
}
